import { useState, useEffect, useRef } from 'react';

export function Hero() {
  const firstLine = 'Tu Socio Tecnológico';
  const secondLine = 'Profesional y Confiable';
  const [displayFirst, setDisplayFirst] = useState('');
  const [displaySecond, setDisplaySecond] = useState('');
  const [phase, setPhase] = useState<'first' | 'second' | 'done'>('first');
  const [showCursor, setShowCursor] = useState(true);
  const [showSubtext, setShowSubtext] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [heroZoom, setHeroZoom] = useState(false);
  const heroRef = useRef<HTMLElement>(null);

  const whatsappMessage = encodeURIComponent('Me contacto del sitio web STI, mi nombre es: ');

  // Efecto zoom-forward después de 2 segundos
  useEffect(() => {
    const zoomTimer = setTimeout(() => {
      setHeroZoom(true);
    }, 2000);
    return () => clearTimeout(zoomTimer);
  }, []);

  useEffect(() => {
    let index = 0;
    const typingInterval = setInterval(() => {
      if (index < firstLine.length) {
        setDisplayFirst(firstLine.slice(0, index + 1));
        index++;
      } else {
        clearInterval(typingInterval);
        setTimeout(() => {
          setPhase('second');
        }, 600);
      }
    }, 140);
    return () => clearInterval(typingInterval);
  }, []);

  useEffect(() => {
    if (phase !== 'second') return;
    let index = 0;
    const typingInterval = setInterval(() => {
      if (index < secondLine.length) {
        setDisplaySecond(secondLine.slice(0, index + 1));
        index++;
      } else {
        clearInterval(typingInterval);
        setPhase('done');
      }
    }, 140);
    return () => clearInterval(typingInterval);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'done') return;
    const subtextTimer = setTimeout(() => setShowSubtext(true), 400);
    const buttonsTimer = setTimeout(() => setShowButtons(true), 1000);
    // Dispatch custom event when typing is done
    const typingDoneTimer = setTimeout(() => {
      window.dispatchEvent(new CustomEvent('heroTypingDone'));
    }, 200);
    return () => {
      clearTimeout(subtextTimer);
      clearTimeout(buttonsTimer);
      clearTimeout(typingDoneTimer);
    };
  }, [phase]);

  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 530);
    return () => clearInterval(cursorInterval);
  }, []);

  // Parallax scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        if (rect.bottom > 0) {
          setScrollY(window.scrollY);
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('contacto');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const cursorElement = (
    <span
      className={`inline-block ml-1 align-baseline transition-opacity duration-100 text-neon-400 ${
        showCursor ? 'opacity-100' : 'opacity-0'
      }`}
      style={{ textShadow: '0 0 8px #00FFF0, 0 0 16px #00FFF0' }}
    >
      _
    </span>
  );

  return (
    <section
      ref={heroRef}
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      <div
        className="absolute inset-0"
        style={{
          transform: `translateY(${scrollY * 0.35}px) scale(${(heroZoom ? 1.22 : 1.0) + scrollY * 0.0003})`,
          willChange: 'transform',
          transition: heroZoom ? 'transform 8s cubic-bezier(0.15, 0.45, 0.35, 0.95)' : 'transform 0.05s linear',
        }}
      >
        <img
          src="https://readdy.ai/api/search-image?query=dark%20modern%20technology%20workspace%20with%20computer%20servers%20and%20network%20equipment%20in%20a%20dark%20room%20with%20subtle%20cyan%20neon%20lighting%20accents%20professional%20IT%20infrastructure%20moody%20atmosphere%20high%20tech%20environment%20with%20glowing%20cyan%20LED%20lights&width=1920&height=1080&seq=1&orientation=landscape"
          alt="Servicio Técnico Informático - Soporte IT profesional en Capital Federal y Zona Norte"
          title="Servicio Técnico Informático STI - Soporte técnico IT profesional"
          width="1920"
          height="1080"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-gray-950/70 via-gray-950/60 to-gray-950/80"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full" style={{ transform: `translateY(${scrollY * 0.12}px)`, opacity: Math.max(1 - scrollY * 0.0012, 0), willChange: 'transform, opacity', transition: 'transform 0.05s linear, opacity 0.05s linear' }}>
        {/* Logo pequeño arriba del título en móvil */}
        <div className="flex justify-center mb-4 sm:mb-6">
          <img
            src="https://static.readdy.ai/image/5390af1b9222ae6e92dc7da25193e2e6/65a0e09d2eafecea725f43c7f76a00d5.png"
            alt="STI Logo - Siempre Conectados"
            title="STI - Servicio Técnico Informático"
            className="w-14 h-14 sm:w-20 sm:h-20 object-contain"
          />
        </div>

        <p className="text-xs sm:text-sm tracking-widest uppercase text-neon-400 mb-3 sm:mb-4 font-medium">Servicio Técnico IT</p>

        <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight">
          <strong className="inline">
            {displayFirst}
            {phase === 'first' && cursorElement}
          </strong>
          <br />
          <span className="text-neon-400 text-neon-glow inline">
            {displaySecond}
            {(phase === 'second' || phase === 'done') && cursorElement}
          </span>
        </h1>

        <p
          className={`text-sm sm:text-lg lg:text-xl text-gray-300 mb-6 sm:mb-8 max-w-3xl mx-auto transition-all duration-700 ease-out ${
            showSubtext ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <strong className="text-white">Servicios integrales de soporte técnico</strong>
          <br />
          Rápido y seguro en{' '}
          <strong className="text-neon-400" style={{ textShadow: '0 0 6px #00FFF0' }}>
            Zona Norte
          </strong>{' '}
          &amp;{' '}
          <strong className="text-neon-400" style={{ textShadow: '0 0 6px #00FFF0' }}>
            CABA
          </strong>
          .
        </p>

        {/* Contador de experiencia - arriba de los botones para visibilidad inmediata */}
        <ExperienceCounters />

        <div
          className={`flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center transition-all duration-700 ease-out ${
            showButtons ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <button
            onClick={scrollToContact}
            className="px-6 sm:px-8 py-3 sm:py-4 bg-gray-800/50 backdrop-blur-sm text-white text-sm sm:text-base font-semibold rounded-lg border border-rosa-400 hover:bg-gray-700/60 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            Comuníquese Ahora
          </button>
        </div>

        <div className="neon-line max-w-md mx-auto mt-8 sm:mt-10 opacity-50"></div>
      </div>
    </section>
  );
}

function ExperienceCounters() {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleTypingDone = () => {
      // Start counter 2 seconds after hero typing finishes
      setTimeout(() => {
        setIsVisible(true);
      }, 2000);
    };
    window.addEventListener('heroTypingDone', handleTypingDone);
    return () => window.removeEventListener('heroTypingDone', handleTypingDone);
  }, []);

  return (
    <div ref={ref} className="mt-6 sm:mt-8 mb-4 sm:mb-6 flex justify-center">
      <CounterItem target={25} suffix="+" label="Años de Experiencia" isVisible={isVisible} delay={0} icon="ri-calendar-check-line" />
    </div>
  );
}

interface CounterItemProps {
  target: number;
  suffix: string;
  label: string;
  isVisible: boolean;
  delay: number;
  icon: string;
}

function CounterItem({ target, suffix, label, isVisible, delay, icon }: CounterItemProps) {
  const [count, setCount] = useState(0);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!isVisible || hasAnimated.current) return;
    hasAnimated.current = true;

    const duration = 2000;
    const startTime = Date.now() + delay;
    const step = () => {
      const now = Date.now();
      if (now < startTime) {
        requestAnimationFrame(step);
        return;
      }
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        setCount(target);
      }
    };
    requestAnimationFrame(step);
  }, [isVisible, target, delay]);

  const formatNumber = (n: number) => {
    if (n >= 1000) return n.toLocaleString('es-AR');
    return n.toString();
  };

  return (
    <div
      className="text-center group"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(25px)',
        transition: `opacity 0.6s ease-out ${delay}ms, transform 0.6s ease-out ${delay}ms`,
      }}
    >
      <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 text-neon-400/70 group-hover:text-neon-400 group-hover:scale-110 transition-all duration-300">
        <i className={`${icon} text-xl`}></i>
      </div>
      <div className="text-2xl sm:text-3xl font-bold text-white mb-1" style={{ textShadow: '0 0 12px rgba(0,255,240,0.3)' }}>
        <span className="text-neon-400">{formatNumber(count)}</span>
        <span className="text-neon-300">{suffix}</span>
      </div>
      <p className="text-xs sm:text-sm text-gray-400 font-medium">{label}</p>
    </div>
  );
}
